import React, { useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { RequireText } from '@/components/atoms/require-text'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { SelectableTimeSelectionType } from '@/models/self-checkin/custom-checkin'
import { StockPerReservationType } from '@/models/custom-checkin-time-selection'
import { SearchReservationTypeWhenCheckedIn, SearchReservationTypeWhenNonCheckIn } from '@/models/reservation'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'
import { FrontTabletRadioButton } from '@/components/molecules/front-tablet/input/radio-button'
import { FrontTabletFontSize } from '@/constants/front-tablet-typography'
import { createSelfCheckinResponsiveStyle, FrontTabletResponsiveToken } from '@/utils/front-tablet-responsive-style'

interface TimeSelectionProps {
  timeSelection: SelectableTimeSelectionType
  onChange: (params: { timeSelectionId: string; selectedTimes: { time: string; day: string }[] }) => void
  selectedTimes: { time: string; day: string }[]
  setIsValidate: (valid: boolean) => void
  selectedReservations: SearchReservationTypeWhenNonCheckIn[] | SearchReservationTypeWhenCheckedIn[]
}

export const TimeSelection: React.FC<TimeSelectionProps> = ({
  timeSelection,
  onChange,
  selectedTimes,
  setIsValidate,
  selectedReservations,
}) => {
  const { t } = useTranslation()
  const pickByLanguage = usePickByLanguage()
  const [radioItems, setRadioItems] = useState<{ [date: string]: Array<{ value: string; label: string; disabled: boolean }> }>()

  const onChangeTime = (e: React.ChangeEvent<HTMLInputElement>, day: string) => {
    const storedIndex = selectedTimes.findIndex(val => val.day === day)
    let cloneSelectedTimes = JSON.parse(JSON.stringify(selectedTimes))
    if (storedIndex === -1) {
      cloneSelectedTimes = [...cloneSelectedTimes, { day, time: e.target.value }]
    } else {
      cloneSelectedTimes[storedIndex].time = e.target.value
    }
    onChange({ timeSelectionId: timeSelection.selection.id, selectedTimes: cloneSelectedTimes })
  }

  const timeToMinutes = (time: string | undefined, intervalMinute: number = 15) => {
    return time ? Number(time.split(':')[0]) * 60 + Number(time.split(':')[1]) : intervalMinute
  }

  const computeSelectableTimes = (date: string) => {
    let nextUsageTime: string
    return Object.keys(timeSelection.mergeAvailabilityTime[date] || {})
      .filter((time, i) => {
        // 開始時間間隔外をフィルター
        const addMinute = timeToMinutes(undefined, timeSelection.selection.usageInterval)
        if (i === 0 || timeToMinutes(nextUsageTime) <= timeToMinutes(time)) {
          nextUsageTime = dayjs(`${date} ${time}`).add(addMinute, 'minute').format('HH:mm')
          return true
        }
        return false
      })
      .filter(time => {
        // 終了時間が枠外であるときにフィルター
        const minUsageMin = timeToMinutes(timeSelection.selection.minUsageTime, timeSelection.selection.usageInterval)
        const endTime = dayjs(`${date} ${time}`)
          .add(minUsageMin - 15, 'minute')
          .format('HH:mm')
        return Object.keys(timeSelection.mergeAvailabilityTime[date]).includes(endTime)
      })
  }

  const isDisabledStartTime = (date: string, time: string): boolean => {
    const usageNumTotal = timeSelection.reservationsByTime[date].reduce((resp, reserveId) => {
      if (timeSelection.selection.stockPerReservation === StockPerReservationType.NumOfPeople) {
        return (resp += selectedReservations?.find(r => r.reservationId === reserveId)?.paxTotal || 1)
      } else if (timeSelection.selection.stockPerReservation === StockPerReservationType.NumOfRoom) {
        return (resp += selectedReservations?.find(r => r.reservationId === reserveId)?.reservationRoom?.length || 1)
      } else {
        return (resp += 1)
      }
    }, 0)

    const minUsageIntervalMinutes = timeToMinutes(timeSelection.selection.minUsageTime, timeSelection.selection.usageInterval)
    const endTime = dayjs(`${date} ${time}`).add(minUsageIntervalMinutes, 'minute').format('HH:mm')
    let runningTime = time
    do {
      if (timeSelection.mergeAvailabilityTime[date][runningTime] < usageNumTotal) {
        return true
      }
      runningTime = dayjs(`${date} ${runningTime}`).add(15, 'minute').format('HH:mm')
    } while (endTime !== runningTime)
    return false
  }

  const makeRadioItems = (date: string) =>
    computeSelectableTimes(date).map(time => ({
      value: time,
      label: time,
      disabled: isDisabledStartTime(date, time),
    }))

  const hasEnabledRadioItems = (_radioItems: Array<{ value: string; label: string; disabled: boolean }>): boolean => {
    return _radioItems.some(item => !item.disabled)
  }

  const validateSelection = (): boolean => {
    if (!timeSelection.selection.isRequired) {
      return true
    }
    const enabledDateNum = Object.keys(radioItems || {}).filter(date => hasEnabledRadioItems(radioItems![date])).length
    return enabledDateNum === selectedTimes.length
  }

  const getTargetGuestName = (date: string): string => {
    if ((selectedReservations?.length || 0) < 2) {
      return ''
    }
    return timeSelection.reservationsByTime[date]
      .map(reserveId => {
        const reserve = selectedReservations.find(r => r.reservationId === reserveId)
        return t('honorific', { name: reserve?.guestName || reserve?.userName || '-' })
      })
      .join(', ')
  }

  useEffect(() => {
    const _radioItems = {}
    let cloneSelectedTimes = JSON.parse(JSON.stringify(selectedTimes))
    Object.keys(timeSelection.mergeAvailabilityTime || {}).map(date => {
      const items = makeRadioItems(date)
      if (!timeSelection.selection.isRequired) {
        items.unshift({ value: '', label: t('Not used'), disabled: false })
      }
      Object.assign(_radioItems, { [date]: items })
      items
        .filter(item => item.disabled)
        .forEach(({ value: time }) => {
          const hasDisabledSelectItem = selectedTimes.some(selectedItem => selectedItem.day === date && selectedItem.time === time)
          if (!hasDisabledSelectItem) {
            return
          }
          cloneSelectedTimes = cloneSelectedTimes.filter(prev => prev.day !== date || prev.time !== time)
        })
    })
    setRadioItems(_radioItems)
    onChange({
      timeSelectionId: timeSelection.selection.id,
      selectedTimes: cloneSelectedTimes,
    })
  }, [])

  useEffect(() => {
    setIsValidate(validateSelection())
  }, [selectedTimes])

  return (
    <div key={timeSelection.selection.id} css={timeSelectionStyle}>
      <h2 className="title">
        {pickByLanguage(timeSelection.selection.title)} {timeSelection.selection.isRequired && <RequireText />}
      </h2>
      <p className="description" dangerouslySetInnerHTML={{ __html: pickByLanguage(timeSelection.selection.description) }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        {Object.keys(radioItems || {}).map((date, i) => {
          const selectedTimeForDate = selectedTimes.find(time => time.day === date)?.time
          return (
            <div key={`ts-at-${date}`}>
              <h3 className="sub-title">
                {dayjs(date).format(t('MM-DD(ddd)'))} {getTargetGuestName(date)}
              </h3>
              <>
                <div css={timeSelectionGroupStyle}>
                  {radioItems![date].map((item, index) => (
                    <div key={item.value || index} css={timeItemWrapperStyle}>
                      <FrontTabletRadioButton
                        id={`time-${date}-${item.value || index}`}
                        label={item.label}
                        formValue={selectedTimeForDate}
                        name={`time-selection-${timeSelection.selection.id}-${date}`}
                        itemCss={timeItemContentStyle}
                        onChange={e => {
                          if (item.disabled) {
                            return
                          }
                          onChangeTime(e, date)
                        }}
                        value={item.value}
                        radioSizeType="small"
                        radioLabelSizeType="small"
                        disabled={item.disabled}
                      />
                    </div>
                  ))}
                </div>
                {!hasEnabledRadioItems(radioItems![date]) && (
                  <div css={timeSelectionErrorStyle}>{t('SelfCheckIn.There is no availability')}</div>
                )}
              </>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const timeSelectionStyle = css(
  {
    '.title': {
      marginBottom: 16,
      fontSize: FrontTabletFontSize.formLabel,
      fontWeight: 'bold',
    },
    '.description': {
      marginBottom: 32,
      fontSize: FrontTabletFontSize.text,
      lineHeight: 1.5,
    },
    '.sub-title': {
      fontSize: FrontTabletFontSize.formInput,
      fontWeight: 'bold',
      marginBottom: 16,
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: {
      '.title': { fontSize: FrontTabletResponsiveToken.formLabelFontSize.landscape13 },
      '.description': { fontSize: 20 },
      '.sub-title': { fontSize: FrontTabletResponsiveToken.formInputFontSize.landscape13 },
    },
  }),
)

const timeSelectionGroupStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 24,
})

const timeItemWrapperStyle = css({
  border: '1px solid #CCCCCC',
  borderRadius: 10,
  minWidth: '190.5px',
  overflow: 'hidden',
})

const timeItemContentStyle = css({
  padding: '16px 24px',
  width: '100%',
  boxSizing: 'border-box',
})

const timeSelectionErrorStyle = css({
  fontSize: 14,
  color: '#FF0000',
  marginTop: 8,
  fontWeight: 'bold',
})
